import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { RichHeading } from '@/components/common/Heading';
import { Image } from '@/components/common/image';
import { Paragraph } from '@/components/common/MarkUp';
import { TestimonialCard } from '@/components/common/TestimonialCard';
import { PortableText } from '@/components/common/utils';

import type { BodyBlock, HeadingWithStyles } from '@/types/shared';
import type { SanityReference } from '@/types/sanity';
import type { SanityImageAsset } from '@/sanity/sanity.types';

interface TestimonialCardProps {
  body: string;
  author?: string;
  authorRole?: string;
  logo?: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
    alternateText?: string;
  };
}

export interface PageTextContentProps {
  heading: HeadingWithStyles;
  text: BodyBlock;
  caption: string;
  formHeaderTitle: string;
  testimonialCard?: TestimonialCardProps;
  image?: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
  };
}

const PageTextContent = ({
  heading,
  text,
  caption,
  testimonialCard,
  image,
}: PageTextContentProps) => {
  const hasExtraContent = !!testimonialCard?.body || !!image;

  return (
    <Container>
      <TextContainer>
        <RichHeading heading={heading} size="display-md" />
        {!!text && (
          <BulletPoints paragraphFontSize="sm">
            <PortableText value={text} />
          </BulletPoints>
        )}
      </TextContainer>
      {hasExtraContent && (
        <ExtraContentWrapper>
          {image?.asset?._ref ? (
            <SingleImage objectFit="contain" alt="" {...image} />
          ) : (
            <TestimonialCard
              image={
                testimonialCard.logo?.asset?._ref && (
                  <Logo {...testimonialCard.logo} objectFit="contain" />
                )
              }
              body={testimonialCard.body}
              author={testimonialCard.author}
              authorCaption={testimonialCard.authorRole}
            />
          )}
        </ExtraContentWrapper>
      )}
      {caption && <Caption size="body-md">{caption}</Caption>}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--spacing-small);
  padding: 0 var(--section-gutter) var(--spacing-large);

  width: 100%;
  margin: var(--spacing-small) 0;

  ${screen.md} {
    margin-top: 0;
    flex: 1;
  }

  ul {
    margin-top: var(--spacing-xx-small, 8px);
  }
`;

const SingleImage = styled(Image)``;

const ExtraContentWrapper = styled.div`
  display: none;

  ${SingleImage} {
    height: 0;
    min-height: 100%;
    width: auto;
    aspect-ratio: auto;
  }

  ${screen.md} {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-small);
    justify-content: flex-end;
    max-height: 450px;
    margin-top: 100px;

    ${SingleImage}, article {
      display: 'flex';
    }
  }
`;

const Logo = styled(Image)`
  object-position: left;
  max-height: 48px;
`;

const TextContainer = styled.div`
  h1 {
    margin-bottom: var(--spacing-x-small);
  }
`;

const Caption = styled(Paragraph)`
  color: var(--text-warm-subtle);
`;

export { PageTextContent };
